import { DashboardChartT, ProtectionT, ScopeT } from 'business/definitions';
import { useMemo } from 'react';
import {
    getBlocks,
    isAllExpression,
    isAnyExpression,
    isBlockExpression,
} from 'helpers/scopes';
import { useSelector } from 'store/hooks';

type ScopeWithProtection = ScopeT & { protections: ProtectionT['blocks'] };

/**
 * Returns the scopes that are necessary to protect the chart data.
 * @param chart - The chart to get the scopes for.
 */
const useGetScopesForChart = (
    chart: DashboardChartT,
): ScopeWithProtection[] => {
    const { scopes, protections } = useSelector((state) => state.vision);
    return useMemo(() => {
        const scopesWithProtection = scopes.map((scope) => {
            const scopeProtections = protections.find(
                (protection) => protection.scope === scope.name,
            );
            return {
                ...scope,
                protections: scopeProtections?.blocks || [],
            };
        });

        const scopesForChart = scopesWithProtection.filter((scope) => {
            const metricFields = chart.metrics.map((metric) => metric.field);
            const dimensionFields = chart.dimensions.map(
                (dimension) => dimension.field,
            );
            const uniqueFields = new Set([...metricFields, ...dimensionFields]);
            return Array.from(uniqueFields).some((field) =>
                scope.protections.includes(field),
            );
        });
        return scopesForChart;
    }, [chart, protections, scopes]);
};

/**
 * Returns the scopes that are necessary to protect the chart data but are not protected by the current filters.
 * This logic should be used in scope alert too.
 * @param chart - The chart to get the scopes for.
 */
export const useGetUnprotectedChartScopes = (
    chart: DashboardChartT,
): ScopeT[] => {
    const scopesForChart = useGetScopesForChart(chart);
    if (scopesForChart.length === 0) {
        return [];
    }

    return scopesForChart.reduce((acc, scope) => {
        const { expression } = scope;
        const { filters } = chart;
        const blocks = getBlocks(scope);
        if (!expression) {
            return [...acc, scope];
        }
        if (isAnyExpression(expression) || isBlockExpression(expression)) {
            const hasOneBlockProtected = filters.some(
                (filter) =>
                    blocks.includes(filter.field) &&
                    filter.value === `scope:${scope.name}`,
            );
            return hasOneBlockProtected ? acc : [...acc, scope];
        }
        if (isAllExpression(expression)) {
            const hasAllBlocksProtected = blocks.every((block) => {
                const blockProtectionFilter = filters.find(
                    (filter) =>
                        filter.field === block &&
                        filter.value === `scope:${scope.name}`,
                );
                return !!blockProtectionFilter;
            });
            return hasAllBlocksProtected ? acc : [...acc, scope];
        }
        return acc;
    }, []);
};
